import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { filterSystemFields } from '@shared/utils/util';
import { JsonBuilderService, OptionsJsonBuilder } from '@shared/components/json-builder/json-builder.service';
import { isArray } from 'util';

@Component({
  templateUrl: './dialog-property.component.html',
  styleUrls: ['./dialog-property.component.scss']
})

export class DialogPropertyComponent implements OnInit {
  public form: FormGroup;
  public fieldsAutoComplete: Observable<OptionsJsonBuilder[]>;
  private fields: OptionsJsonBuilder[] = [];
  private usedKeys = [];
  private data;
  private root;
  private rootType;
  public breadcrumb;

  constructor(
    private dialogRef: MatDialogRef<DialogPropertyComponent>,
    private dataComponent: DialogComponent,
    private formBuilder: FormBuilder,
    private jsonBuilderService: JsonBuilderService
  ) {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      value_type: ['', Validators.required],
      value: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.fields = this.jsonBuilderService.getOptions();
    this.data = JSON.parse(JSON.stringify(this.dataComponent.data.data));
    this.root = this.dataComponent.data.data.root;
    if (isArray(this.root)) {
      this.rootType = 'array';
    } else {
      this.rootType = 'object';
      this.usedKeys = Object.keys(this.root);
    }
    this.breadcrumb = this.dataComponent.data.breadcrumb;
    delete this.data.root;
    this.handleNameField();
    this.setChangeValueType();
    this.setValueOnForm();
  }

  private handleNameField() {
    if (this.rootType === 'object') {
      this.setCustomValidatorName();
    } else {
      this.f.name.disable();
    }
  }

  private setValueOnForm() {
    const name = this.data.name ?? "";
    const value_type = this.getValueType(this.data.value);
    const value = value_type === "1" ? this.fields.find(option => option.variable_key === this.data.value) : this.data.value;

    this.form.patchValue({ name, value_type, value });
  }

  private getValueType(value: string): "1" | "2" {
    return value && value.startsWith('#{@') && value.endsWith('@}') ? "1" : "2";
  }

  private getVariableKey(option: OptionsJsonBuilder): string {
    return option.variable_key;
  }

  private setChangeValueType() {
    this.f.value_type.valueChanges.subscribe((value) => {
      if (value == '1') {
        this.setAutoCompleteFields();
      } else {
        this.removeAutoCompleteFields();
      }
      this.handleValidatorFieldValue(value);
      this.f.value.setValue('');
      this.f.value.markAsUntouched();
    });
  }

  private validatorCheckIsField(input) {
    const value = input.value;
    if (typeof (value) === 'string') {
      return { unselectedField: true };
    }

    return null;
  }

  private handleValidatorFieldValue(valueType) {
    this.f.value.clearValidators();
    if (valueType === "1") {
      this.f.value.setValidators([Validators.required, this.validatorCheckIsField])
    } else {
      this.f.value.setValidators(Validators.required);
    }

    this.f.value.updateValueAndValidity();
  }

  private setCustomValidatorName() {
    this.f.name.setValidators([Validators.required, validatorKey(this.usedKeys, this.data.name)]);
    this.f.name.updateValueAndValidity();
  }

  private setAutoCompleteFields() {
    this.fieldsAutoComplete = this.f.value.valueChanges
      .pipe(
        startWith(''),
        map(option => option ? filterSystemFields({ value: option, systemFields: this.fields }) as unknown as OptionsJsonBuilder[] : this.fields.slice())
      );
  }

  private removeAutoCompleteFields() {
    this.f.value.valueChanges.pipe();
  }

  public displayField(field): string | undefined {
    return field ? field.label : undefined;
  }

  onSubmit() {
    const data = {
      name: this.f.name.value,
      value: this.f.value_type.value === "1" ? this.getVariableKey(this.f.value.value) : this.f.value.value
    }

    if (this.rootType === 'object') {
      this.root[data.name] = data.value;

      if (data.name !== this.data.name) {
        delete this.root[this.data.index];
      }
    } else {
      if (this.data.index === undefined) {
        this.root.push(data.value)
      } else {
        this.root[this.data.index] = data.value;
      }
    }

    this.dialogRef.close();
  }

  get f() {
    return this.form.controls;
  }
}

function validatorKey(usedKeys, initialKeyName) {
  return (control: AbstractControl) => {
    if (control.value && control.value !== "") {
      if (usedKeys.includes(control.value) && control.value !== initialKeyName) {
        return { keyAlreadyExists: true }
      }
    }
    return null;
  }
}
